/**
 * The small Markdown subset used by level statements.
 *
 * Statements are authored content, but rendering them into an HTML string
 * means keeping an escaping step between every pair of tags and hoping nobody
 * ever forgets one. Tokenising instead removes that class of bug entirely: the
 * text stays data all the way to Svelte, which escapes it, and the interface is
 * free to render a name as a link without a second, unescaped pass.
 *
 * The subset is deliberately tiny: paragraphs, inline code, bold, and fenced
 * blocks. A blank line starts a paragraph; a single newline inside one is soft,
 * as in Markdown, so a statement can be written over several lines and still be
 * wrapped by the browser rather than by hand.
 */

/** A run of text inside a paragraph. */
type InlinePart =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'code'; text: string };

/** A paragraph, or a fenced block. */
type StatementBlock =
  | { kind: 'paragraph'; parts: InlinePart[] }
  | { kind: 'code'; text: string };

/** Splits a paragraph into plain runs, bold runs and code spans. */
function inline(text: string): InlinePart[] {
  const parts: InlinePart[] = [];
  let cursor = 0;
  let index = 0;

  const flush = (end: number): void => {
    if (end > cursor) {
      parts.push({ kind: 'text', text: text.slice(cursor, end) });
    }
  };

  while (index < text.length) {
    if (text[index] === '`') {
      const close = text.indexOf('`', index + 1);
      if (close !== -1) {
        flush(index);
        parts.push({ kind: 'code', text: text.slice(index + 1, close) });
        index = close + 1;
        cursor = index;
        continue;
      }
    } else if (text.startsWith('**', index)) {
      const close = text.indexOf('**', index + 2);
      if (close !== -1) {
        flush(index);
        parts.push({ kind: 'strong', text: text.slice(index + 2, close) });
        index = close + 2;
        cursor = index;
        continue;
      }
    }
    index += 1;
  }

  flush(text.length);
  return parts;
}

/** Converts a level statement into blocks. */
export function parseStatement(markdown: string): StatementBlock[] {
  const blocks: StatementBlock[] = [];
  // An odd number of fences leaves an unterminated block, which is rendered as
  // written rather than silently dropped.
  const segments = markdown.split('```');
  segments.forEach((segment, index) => {
    if (index % 2 === 1) {
      blocks.push({ kind: 'code', text: segment.trim() });
      return;
    }
    for (const paragraph of segment.split(/\n{2,}/)) {
      const text = paragraph.trim().replace(/\s*\n\s*/g, ' ');
      if (text !== '') {
        blocks.push({ kind: 'paragraph', parts: inline(text) });
      }
    }
  });
  return blocks;
}
